// @flow

import React, { PureComponent } from 'react';
import { i18n } from 'shared/utils';
import { graphql, fetchQuery } from 'react-relay';
import relayEnvironment from 'shared/gql/relayEnvironment';
import { FieldGroup } from 'shared/components/form';

type Props = {
  name?: string,
  rawField?: boolean,
  label?: ?string,
  error?: ?string,
  queryVariables?: ?Object,
  excludeIds?: Array<string>,
  isSearchable?: boolean,
  filterPublishedWorkflows?: boolean,
};

type State = {
  fetchedOptions: Array<Object>,
};

const QUERY = graphql`
  query SelectWorkflowFieldQuery {
    workflows {
      edges {
        node {
          id
          name
          states {
            id
            isGangRun
          }
        }
      }
    }
  }
`;

class SelectWorkflowField extends PureComponent<Props, State> {
  static defaultProps = {
    name: undefined,
    rawField: false,
    label: undefined,
    error: undefined,
    queryVariables: {},
    excludeIds: [],
    isSearchable: undefined,
    filterPublishedWorkflows: true,
  };

  mounted = false;

  state = {
    fetchedOptions: [],
  };

  componentDidMount() {
    this.mounted = true;

    this.getData();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  getData = async (inputValue?: string) => {
    const { queryVariables, excludeIds } = this.props;

    try {
      const results = await fetchQuery(relayEnvironment, QUERY, {
        search: inputValue,
        ...queryVariables,
      });

      const options = this.massageData(
        results.workflows.edges || [],
        excludeIds,
      );

      if (this.mounted) {
        this.setState({
          fetchedOptions: options,
        });
      }

      return options;
    } catch (e) {
      return [];
    }
  };

  /**
   * Massage records to be ReactSelect-friendly
   * @param {Array<Object>} records
   * @param {Array<string | number>} excludeIds
   * @return {Array<Object>}
   */
  massageData = (
    records: Array<Object>,
    excludeIds?: Array<string> = [],
  ): Array<Object> => {
    const { filterPublishedWorkflows } = this.props;

    return records
      .filter(a => {
        if (filterPublishedWorkflows) {
          // filter out specified ids and unpublished Workflows with a.node?.states?.length === 0
          return a.node?.states?.length === 0
            ? false
            : !a.node || excludeIds.indexOf(a.node.id.toString()) < 0;
        }

        // filter out specified ids
        return !a.node || excludeIds.indexOf(a.node.id.toString()) < 0;
      })
      .map(({ node }) => ({
        data: {
          id: node.id,
          name: node.name,
          hasGangRun: node.states.some(state => state.isGangRun),
        },
        value: node.id,
        label: node.name,
      }));
  };

  render() {
    const { name, rawField, label, error, ...fieldProps } = this.props;
    const { fetchedOptions } = this.state;

    return (
      <FieldGroup
        name={name}
        rawField={rawField || false}
        label={label !== undefined ? label : i18n.t('Workflow')}
        error={error}
        clearable
        placeholder={i18n.t('Workflows')}
        {...fieldProps}
        options={fetchedOptions}
        type="select"
        // loadOptions={this.getData}
        defaultOptions
      />
    );
  }
}

export default SelectWorkflowField;
